import { track, useTracking } from 'lib/analytics'
import useOnCurrentUserLoaded from 'shared/hooks/useOnCurrentUserLoaded/useOnCurrentUserLoaded'
import editorialPageSections from './sections/editorialPageSections'
import generalLandingPageSections from './sections/generalLandingPageSections'
import showroomSections from './sections/showroomSections'
import productReviewSections from './sections/productReviewSections'
import ambassadorSections from './sections/ambassadorSections'
import holidaySections from './sections/holidaySections'
import whyBabylistPageSections from './sections/whyBabylistSection'
import { PageConfig } from './LandingPage.types'

const pagesConfig: { [key: string]: PageConfig } = {
  showroom: {
    title: 'Babylist Showroom Los Angeles | Baby Store of the Future',
    description: `Shop, try, and register for baby products in real life at Babylist's new immersive showroom in Beverly Hills.`,
    canonicalUrl: 'https://www.babylist.com/lp/showroom',
    additionalHelmetProps: undefined,
    sections: showroomSections,
  },
  'registry-acqex': {
    title: 'Babylist - The Best Baby Registry',
    description: `Add absolutely anything to your registry. We'll have your back along the way.`,
    canonicalUrl: 'https://www.babylist.com/lp/registry-acqex',
    additionalHelmetProps: <meta content="noindex" name="robots" />,
    sections: generalLandingPageSections,
  },
  'editorial-policies-guidelines': {
    title: 'Babylist Editorial Policy',
    description: `Babylist's editorial policy for product recommendations.`,
    canonicalUrl: 'https://www.babylist.com/lp/editorial-policies-guidelines',
    additionalHelmetProps: undefined,
    sections: editorialPageSections,
  },
  'product-review-processes': {
    title: 'Babylist Product Reviews Policy, Guidelines, and Disclosures',
    description: `Babylist policies and ethics on product reviews. Meet our team of product review experts. Learn how we choose, test, and review products, and how we make money.`,
    canonicalUrl: 'https://www.babylist.com/lp/product-review-processes',
    additionalHelmetProps: undefined,
    sections: productReviewSections,
  },
  ambassador: {
    title: 'Become a Babylist Ambassador | Babylist',
    description: `Start earning Babylist Shop credit by tagging @babylist on Instagram or TikTok!`,
    canonicalUrl: 'https://www.babylist.com/lp/ambassador',
    additionalHelmetProps: (
      <script
        src="https://pub.loudcrowd.com/launch.js?hid=cHJvZ3JhbV9ob3N0OjQxMA"
        type="text/javascript"
      />
    ),
    sections: ambassadorSections,
  },
  holiday: {
    title: 'Babylist Holiday Gift Ideas 2023 | Babylist',
    description:
      'Find holiday gift ideas for babies, toddlers and your family fast. Read holiday gift guides, create a holiday wishlist and buy baby gift cards in one easy place.',
    canonicalUrl: 'https://www.babylist.com/lp/holiday',
    additionalHelmetProps: undefined,
    sections: holidaySections,
    onPageLoad: (currentUser, tracker) => {
      if (typeof window !== 'undefined') {
        tracker.trackEvent({
          event: track.pageViewed,
          eventLocation: track.EventLocation.HOLIDAY_LANDING_PAGE,
          eventUrl: window.location.href,
          registryOwnerId: currentUser?.id,
          registryId: currentUser?.registryId,
        })
      }
    },
  },
  // TODO: update title, description. For now, I am copying values from the old /why-babylist page
  'why-babylist': {
    title: 'Universal Baby Registry | Babylist Benefits',
    description: `Babylist's universal baby registry lets you add anything from any website. Learn why Babylist is the best baby registry for growing families. Build your registry now.`,
    canonicalUrl: 'https://www.babylist.com/lp/why-babylist',
    sections: whyBabylistPageSections,
  },
}

export const usePage = (slug: string): PageConfig | undefined =>
  pagesConfig[slug]

export const useOnPageLoad = (pageConfig: PageConfig | undefined) => {
  const tracker = useTracking()

  useOnCurrentUserLoaded((currentUser) => {
    if (pageConfig && typeof pageConfig?.onPageLoad === 'function') {
      pageConfig.onPageLoad(currentUser, tracker)
    }
  })
}
